import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, Users, BookOpen, Award, Calendar, Star, Search, Database, X } from 'lucide-react';

const sidebarSections = [
  {
    id: 'research',
    title: 'Research & Communities',
    icon: <Users className="w-5 h-5" />,
    color: 'bg-blue-500',
    items: [
      {
        id: 1,
        title: "Research Communities",
        icon: <Users className="w-4 h-4" />,
        description: "Join active research groups",
        badge: "12 Active",
        color: "bg-blue-400"
      },
      {
        id: 2,
        title: "Collaboration Hub",
        icon: <Users className="w-4 h-4" />,
        description: "Team projects and partnerships",
        badge: "Active",
        color: "bg-teal-400"
      },
      {
        id: 3,
        title: "Featured Research",
        icon: <Star className="w-4 h-4" />,
        description: "Trending research topics",
        badge: "Hot",
        color: "bg-orange-400"
      }
    ]
  },
  {
    id: 'publications',
    title: 'Publications & Resources',
    icon: <BookOpen className="w-5 h-5" />,
    color: 'bg-green-500',
    items: [
      {
        id: 4,
        title: "Latest Publications",
        icon: <BookOpen className="w-4 h-4" />,
        description: "Recent papers and articles",
        badge: "New",
        color: "bg-green-400"
      },
      {
        id: 5,
        title: "Knowledge Base",
        icon: <Database className="w-4 h-4" />,
        description: "Documentation library",
        badge: "100+ Docs",
        color: "bg-pink-400"
      },
      {
        id: 6,
        title: "Research Tools",
        icon: <Search className="w-4 h-4" />,
        description: "Analysis and visualization tools",
        badge: "6 Tools",
        color: "bg-indigo-400"
      }
    ]
  },
  {
    id: 'achievements',
    title: 'Achievements & Events',
    icon: <Award className="w-5 h-5" />,
    color: 'bg-yellow-500',
    items: [
      {
        id: 7,
        title: "Awards & Recognition",
        icon: <Award className="w-4 h-4" />,
        description: "Achievement highlights",
        badge: "5 Awards",
        color: "bg-yellow-400"
      },
      {
        id: 8,
        title: "Upcoming Events",
        icon: <Calendar className="w-4 h-4" />,
        description: "Conferences and seminars",
        badge: "3 Events",
        color: "bg-purple-400"
      }
    ]
  }
];

const AutoScrollSidebar = ({ isOpen = false, onClose = () => {} }) => {
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [scrollDirection, setScrollDirection] = useState(1); // 1 for down, -1 for up
  const [expandedSections, setExpandedSections] = useState({});
  const scrollContainerRef = useRef(null);
  const autoScrollRef = useRef(null);


  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoScrolling) return;

    const startAutoScroll = () => {
      autoScrollRef.current = setInterval(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const scrollAmount = 1; // Smooth slow scrolling
          
          // Calculate new scroll position
          const newScrollTop = container.scrollTop + (scrollAmount * scrollDirection);
          
          // Check boundaries and reverse direction
          if (newScrollTop >= container.scrollHeight - container.clientHeight) {
            setScrollDirection(-1); // Scroll up
          } else if (newScrollTop <= 0) {
            setScrollDirection(1); // Scroll down
          }
          
          container.scrollTop = newScrollTop;
        }
      }, 50); // Very smooth scrolling
    };

    startAutoScroll();

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isAutoScrolling, scrollDirection]);

  // Handle user interaction
  const handleMouseEnter = () => {
    setIsAutoScrolling(false);
  };

  const handleMouseLeave = () => {
    // Resume auto-scroll after 2 seconds of no interaction
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 2000);
  };

  const handleTouchStart = () => {
    setIsAutoScrolling(false);
  };

  const handleTouchEnd = () => {
    // Resume auto-scroll after 3 seconds on mobile
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 3000);
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Initialize all sections as expanded
  useEffect(() => {
    const initialExpanded = {};
    sidebarSections.forEach(section => {
      initialExpanded[section.id] = true;
    });
    setExpandedSections(initialExpanded);
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed lg:sticky top-0 inset-y-0 left-0 z-30
        transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-transform duration-300 ease-in-out
        w-[280px] sm:w-80 lg:w-64 xl:w-72
        bg-white rounded-none lg:rounded-lg shadow-xl border-r lg:border border-gray-200 
        overflow-hidden flex-shrink-0
        h-screen lg:h-[calc(100vh-2rem)] lg:my-4 lg:ml-4
      `}>
        {/* Header */}
        <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white relative">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-bold">Research Hub</h2>
              <p className="text-blue-100 text-xs sm:text-sm mt-1">Your academic companion</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isAutoScrolling ? 'bg-green-400 animate-pulse' : 'bg-gray-300'}`}></div>
              {/* Close button for mobile */}
              <button
                onClick={onClose}
                className="lg:hidden p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto scrollbar-hide"
          style={{
            height: 'calc(100vh - 180px)', // Adjust for header and footer
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Duplicate sections for infinite scroll effect */}
          {[...sidebarSections, ...sidebarSections].map((section, sectionIndex) => (
            <div key={`${section.id}-${sectionIndex}`} className="border-b border-gray-100 last:border-b-0">
              {/* Section Header */}
              <div
                className="p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all duration-200 group"
                onClick={() => toggleSection(`${section.id}-${sectionIndex}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`p-1.5 sm:p-2 rounded-lg ${section.color} text-white group-hover:scale-105 transition-transform duration-200`}>
                      {section.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-xs sm:text-sm group-hover:text-blue-600 transition-colors">
                        {section.title}
                      </h3>
                      <p className="text-gray-500 text-xs hidden sm:block">
                        {section.items.length} items
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {expandedSections[`${section.id}-${sectionIndex}`] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-all duration-200" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-all duration-200" />
                    )}
                  </div>
                </div>
              </div>

              {/* Section Items */}
              {expandedSections[`${section.id}-${sectionIndex}`] && (
                <div className="bg-white">
                  {section.items.map((item, itemIndex) => (
                    <div
                      key={`${item.id}-${sectionIndex}-${itemIndex}`}
                      className="pl-4 sm:pl-6 pr-3 sm:pr-4 py-2 sm:py-3 border-b border-gray-50 last:border-b-0 hover:bg-blue-50 transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <div className={`p-1 sm:p-1.5 rounded-md ${item.color} text-white group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
                          {item.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900 text-xs sm:text-sm group-hover:text-blue-600 transition-colors line-clamp-1">
                              {item.title}
                            </h4>
                            <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-200 flex-shrink-0 ml-2" />
                          </div>
                          <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                            {item.description}
                          </p>
                          {item.badge && (
                            <span className="inline-block mt-1 sm:mt-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              {item.badge}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs sm:text-sm">
            <span className="text-gray-600">Auto-scroll</span>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isAutoScrolling ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className={`text-xs ${isAutoScrolling ? 'text-green-600' : 'text-gray-500'}`}>
                {isAutoScrolling ? 'Active' : 'Paused'}
              </span>
            </div>
          </div>
          <div className="mt-1 sm:mt-2 text-xs text-gray-500">
            {Object.values(expandedSections).filter(Boolean).length} sections expanded
          </div>
        </div>

        {/* Custom CSS for hiding scrollbar */}
        <style jsx>{`
          .scrollbar-hide {
            -webkit-scrollbar: none;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .line-clamp-1 {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default AutoScrollSidebar;